import { FC, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Autocomplete } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import type { RequirementThread } from './types';
import {
  DESCRIPTION_MAX,
  EXTERNAL_REF_MAX,
  NAME_MAX,
  OWNER_MAX,
  changedMetadata,
  charCount,
  validateMetadata,
  type FieldError,
  type MetadataErrors,
  type MetadataValues,
} from './metadataValidation';

interface MetadataEditorProps {
  thread: RequirementThread;
  busy: boolean;
  /** Saves the changed fields only. Resolves false when the save failed. */
  onSave: (patch: Partial<MetadataValues>) => Promise<boolean>;
  onCancel: () => void;
}

const useStyles = makeStyles(theme => ({
  fields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
  },
  wide: { gridColumn: '1 / -1' },
  hint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    margin: theme.spacing(0.75, 0),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));

function valuesOf(thread: RequirementThread): MetadataValues {
  return {
    name: thread.name,
    externalRef: thread.externalRef,
    description: thread.description,
    owner: thread.owner,
    tags: thread.tags,
  };
}

/** Surrounding spaces are not part of a value: "Auth " saves as "Auth". */
function trimValues(values: MetadataValues): MetadataValues {
  return {
    name: values.name.trim(),
    externalRef: values.externalRef.trim(),
    description: values.description.trim(),
    owner: values.owner.trim(),
    tags: values.tags.map(tag => tag.trim()).filter(tag => tag !== ''),
  };
}

/**
 * Inline form for a requirement's name, external reference, description, owner
 * and tags. Saving changes the thread only and creates no text version.
 */
export const MetadataEditor: FC<MetadataEditorProps> = ({
  thread,
  busy,
  onSave,
  onCancel,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('requirement-lifecycle');

  // The values when the form opened, kept fixed. A refresh of the thread while
  // the form is open (another tab, a reload after another action) must not
  // move this baseline: an untouched field would then differ from it and be
  // sent with its old value, overwriting the newer one. Only fields the user
  // changed are sent.
  const [initial] = useState<MetadataValues>(() => valuesOf(thread));
  const [values, setValues] = useState<MetadataValues>(initial);
  const [saveFailed, setSaveFailed] = useState(false);
  // Ties each label to its input, for screen readers and getByLabelText.
  const idPrefix = `thread-${thread.threadId}-metadata`;

  const patch = changedMetadata(trimValues(initial), trimValues(values));
  // Only the fields being changed are checked, as on the backend: a value
  // stored before the limits existed must not block an edit of another field.
  const errors = Object.fromEntries(
    Object.entries(validateMetadata(trimValues(values))).filter(
      ([field]) => field in patch,
    ),
  ) as MetadataErrors;
  const hasErrors = Object.keys(errors).length > 0;
  const hasChanges = Object.keys(patch).length > 0;

  const set = <K extends keyof MetadataValues>(
    field: K,
    value: MetadataValues[K],
  ) => setValues(prev => ({ ...prev, [field]: value }));

  const message = (error?: FieldError) =>
    error ? t(`detail.metadata.errors.${error.key}`, error.params) : undefined;

  // The length is shown under a limited field, so the limit is visible before
  // it is reached; an error replaces it. It counts what is saved, without the
  // surrounding spaces, so it agrees with the check.
  // (`length`, not `count`: i18next reads `count` as a plural selector.)
  const helper = (error: FieldError | undefined, value: string, max: number) =>
    message(error) ??
    t('detail.metadata.count', { length: charCount(value.trim()), max });

  const save = async () => {
    setSaveFailed(false);
    const ok = await onSave(patch);
    if (!ok) setSaveFailed(true);
  };

  return (
    <Box>
      <Box className={classes.fields}>
        <TextField
          id={`${idPrefix}-name`}
          label={t('detail.metadata.fields.name')}
          required
          variant="outlined"
          size="small"
          value={values.name}
          onChange={e => set('name', e.target.value)}
          error={Boolean(errors.name)}
          helperText={helper(errors.name, values.name, NAME_MAX)}
          disabled={busy}
        />
        <TextField
          id={`${idPrefix}-externalRef`}
          label={t('detail.metadata.fields.externalRef')}
          variant="outlined"
          size="small"
          value={values.externalRef}
          onChange={e => set('externalRef', e.target.value)}
          error={Boolean(errors.externalRef)}
          helperText={helper(
            errors.externalRef,
            values.externalRef,
            EXTERNAL_REF_MAX,
          )}
          disabled={busy}
        />
        <TextField
          className={classes.wide}
          id={`${idPrefix}-description`}
          label={t('detail.metadata.fields.description')}
          variant="outlined"
          size="small"
          multiline
          minRows={3}
          value={values.description}
          onChange={e => set('description', e.target.value)}
          error={Boolean(errors.description)}
          helperText={helper(
            errors.description,
            values.description,
            DESCRIPTION_MAX,
          )}
          disabled={busy}
        />
        <TextField
          id={`${idPrefix}-owner`}
          label={t('detail.metadata.fields.owner')}
          variant="outlined"
          size="small"
          value={values.owner}
          onChange={e => set('owner', e.target.value)}
          error={Boolean(errors.owner)}
          helperText={helper(errors.owner, values.owner, OWNER_MAX)}
          disabled={busy}
        />
        {/* Free text: Enter adds the tag, and a tag still being typed is
            added when the field loses focus, so it is not lost on Save. */}
        <Autocomplete
          id={`${idPrefix}-tags`}
          multiple
          freeSolo
          autoSelect
          options={[] as string[]}
          value={values.tags}
          onChange={(_, tags) => set('tags', tags as string[])}
          disabled={busy}
          renderTags={(tags, getTagProps) =>
            tags.map((tag, index) => (
              <Chip
                {...getTagProps({ index })}
                key={tag}
                label={tag}
                size="small"
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              label={t('detail.metadata.fields.tags')}
              variant="outlined"
              size="small"
              error={Boolean(errors.tags)}
              helperText={message(errors.tags) ?? t('detail.metadata.tagsHint')}
            />
          )}
        />
      </Box>
      <Typography className={classes.hint}>
        {t('detail.metadata.hint')}
      </Typography>
      {saveFailed && <Alert severity="error">{t('detail.editError')}</Alert>}
      <Box className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={busy || !hasChanges || hasErrors}
          onClick={save}
          endIcon={busy ? <CircularProgress size={18} /> : undefined}
        >
          {t('detail.actions.save')}
        </Button>
        <Button size="small" disabled={busy} onClick={onCancel}>
          {t('detail.actions.cancel')}
        </Button>
      </Box>
    </Box>
  );
};

export default MetadataEditor;
